import { useRef, useState } from "react";
import { ChevronDown, GripVertical, RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SegmentedControl, SegmentedControlItem } from "@/components/ui/segmented-control";
import { LIST_BEHAVIORS, setListBehavior, useListBehavior } from "./listBehavior";
import {
  SELECTION_BEHAVIORS,
  setSelectionBehavior,
  useSelectionBehavior,
} from "./selectionBehavior";
import {
  BULK_ACTION_BEHAVIORS,
  setBulkActionBehavior,
  useBulkActionBehavior,
} from "./bulkActionBehavior";
import { resetPrototype } from "./mock";
import { SCENARIO_SECTIONS, activeScenario, storeScenario } from "./scenarios";
import { CONTRACT_COUNTS, activeContractCountOption, storeContractCount } from "./testData";

/**
 * Prototype-only affordance: reset the mock data, and jump straight into any of
 * the test scenarios.
 *
 * Each scenario re-seeds the mock and lands on the screen where that state is
 * visible, so a reviewer never has to click their way into a case. Switching is
 * a full reload because the mock seeds its catalogue once, at module load.
 *
 * Floating, draggable by the grip, and collapsible — the scenario list is long
 * and there is nothing to read once one is picked. Mounted globally but only
 * when the dev auth bypass is active (see App.tsx).
 */
const POS_KEY = "brs-dev-bar-pos";
const COLLAPSED_KEY = "brs-dev-bar-collapsed";

function loadPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through to default */
  }
  return { x: 12, y: 12 };
}

export function PrototypeDevBar() {
  const [pos, setPos] = useState(loadPos);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "true");
  const posRef = useRef(pos);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const scenario = activeScenario();
  const listBehavior = useListBehavior();
  const selectionBehavior = useSelectionBehavior();
  const bulkActionBehavior = useBulkActionBehavior();
  const contractCount = activeContractCountOption();

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const x = Math.max(0, Math.min(window.innerWidth - 120, d.ox + (e.clientX - d.sx)));
    const y = Math.max(0, Math.min(window.innerHeight - 40, d.oy + (e.clientY - d.sy)));
    posRef.current = { x, y };
    setPos(posRef.current);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (drag.current) localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
    drag.current = null;
  };

  const handleReset = () => {
    resetPrototype();
    window.location.reload();
  };

  const handleScenario = (id: string, entryPath: string) => {
    storeScenario(id);
    // Full navigation: the mock seeds its catalogue from the scenario at load.
    window.location.assign(entryPath);
  };

  const handleContractCount = (count: number | null) => {
    storeContractCount(count);
    // Full reload: the mock seeds its catalogue once, at module load.
    window.location.reload();
  };

  const toggleCollapsed = () => {
    setCollapsed((wasCollapsed) => {
      localStorage.setItem(COLLAPSED_KEY, String(!wasCollapsed));
      return !wasCollapsed;
    });
  };

  return (
    <div
      className="fixed z-[100] flex w-64 flex-col gap-2 rounded-md border border-border bg-background p-2 shadow-md"
      style={{ left: pos.x, top: pos.y }}
    >
      {/* The only row that stays horizontal: grip, reset, collapse. Everything
          below stacks, so the bar stays a narrow column however many dials it
          grows — across the top they ran into each other. */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Drag to move"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="flex cursor-grab touch-none items-center text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="size-4" />
        </button>

        {!collapsed && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="flex-1 justify-start pl-1"
            onClick={handleReset}
          >
            <RotateCcw className="size-4 text-muted-foreground" />
            Reset prototype
          </Button>
        )}

        <button
          type="button"
          aria-label={collapsed ? "Expand dev bar" : "Collapse dev bar"}
          onClick={toggleCollapsed}
          className="ml-auto flex items-center px-1 text-muted-foreground"
        >
          <ChevronDown className={collapsed ? "size-4 -rotate-90" : "size-4"} />
        </button>
      </div>

      {!collapsed && (
        <>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="secondary" size="sm" className="w-full justify-start">
                Scenario: {scenario.label}
                {/* Carried on the trigger too, so the flag is visible while
                    sitting in the scenario, not only while picking one. */}
                {scenario.inProgress && (
                  <Badge variant="orange" size="sm">
                    In progress
                  </Badge>
                )}
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              /* Beside the bar, not under it: the bar is a column now, so a
                 menu opening downwards lands on top of its own dials. */
              side="right"
              align="start"
              sideOffset={8}
              className="z-[110] max-h-[70vh] w-80 overflow-y-auto"
            >
              {/* Two sections, not four. Grouping by which dial a scenario moves
                  — revenue, catalogue, period — split things a reviewer thinks
                  of together. This split is one they do think in: what the run
                  is set up on, and what it looks like once it has started. */}
              {SCENARIO_SECTIONS.map((section, sectionIndex) => (
                <div key={section.title}>
                  {sectionIndex > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel>{section.title}</DropdownMenuLabel>
                  {section.scenarios.map((s) => (
                    <DropdownMenuItem
                      key={s.id}
                      onClick={() => handleScenario(s.id, s.entryPath)}
                      className="flex flex-col items-start gap-0.5"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        {s.label}
                        {s.id === scenario.id ? " ·" : ""}
                        {/* Flagged in the picker rather than left to be found:
                            this state is still being designed. */}
                        {s.inProgress && (
                          <Badge variant="orange" size="sm">
                            In progress
                          </Badge>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">{s.description}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* The dials being compared, one per row. They live up here rather
              than in the drawer: they are for comparing variants, not part of
              the design being reviewed. */}
          <div className="flex flex-col gap-1 border-t border-border pt-2">
            <span className="text-xs font-medium text-muted-foreground">List behavior</span>
            <SegmentedControl
              className="w-full"
              type="single"
              value={listBehavior}
              onValueChange={(next) => next && setListBehavior(next as typeof listBehavior)}
            >
              {LIST_BEHAVIORS.map((behavior) => (
                <SegmentedControlItem
                  key={behavior.id}
                  value={behavior.id}
                  className="flex-1 whitespace-nowrap"
                >
                  {behavior.label}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </div>

          <div className="flex flex-col gap-1 border-t border-border pt-2">
            <span className="text-xs font-medium text-muted-foreground">Selection</span>
            <SegmentedControl
              className="w-full"
              type="single"
              value={selectionBehavior}
              onValueChange={(next) =>
                next && setSelectionBehavior(next as typeof selectionBehavior)
              }
            >
              {SELECTION_BEHAVIORS.map((behavior) => (
                <SegmentedControlItem
                  key={behavior.id}
                  value={behavior.id}
                  className="flex-1 whitespace-nowrap"
                >
                  {behavior.label}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </div>

          <div className="flex flex-col gap-1 border-t border-border pt-2">
            <span className="text-xs font-medium text-muted-foreground">
              Bulk actions while searching
            </span>
            <SegmentedControl
              className="w-full"
              type="single"
              value={bulkActionBehavior}
              onValueChange={(next) =>
                next && setBulkActionBehavior(next as typeof bulkActionBehavior)
              }
            >
              {BULK_ACTION_BEHAVIORS.map((behavior) => (
                <SegmentedControlItem
                  key={behavior.id}
                  value={behavior.id}
                  className="flex-1 whitespace-nowrap"
                >
                  {behavior.label}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="secondary" size="sm" className="w-full justify-start">
                Test data: {contractCount.label}
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="start"
              sideOffset={8}
              className="z-[110] w-80"
            >
              <DropdownMenuLabel>Contracts in the catalogue</DropdownMenuLabel>
              {CONTRACT_COUNTS.map((option) => (
                <DropdownMenuItem
                  key={option.label}
                  onClick={() => handleContractCount(option.count)}
                  className="flex flex-col items-start gap-0.5"
                >
                  <span className="text-sm font-medium">
                    {option.label}
                    {option.count === contractCount.count ? " ·" : ""}
                  </span>
                  <span className="text-xs text-muted-foreground">{option.description}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}

    </div>
  );
}
